/**
 * cart-you-may-like.js
 * Custom element: <cart-recs-carousel>
 * Fetches 3 hardcoded products by handle and renders them as a carousel.
 * The section is hidden (by Liquid server-side and JS client-side) when
 * any of those products are already in the cart.
 *
 * Hardcoded handles:
 *   facial-treatment-essence
 *   first-experience-kit
 *   pitera-ultimate-aura-essentials-kit
 */

const YMAL_HANDLES = [
  'facial-treatment-essence',
  'first-experience-kit',
  'pitera-ultimate-aura-essentials-kit'
];

function getKcpSettings(rootEl) {
  const configEl = document.getElementById('algolia-kcp-config');
  const enabledValue = rootEl?.dataset?.kcpEnabled ?? configEl?.dataset?.enabled ?? 'false';
  const discountValue = rootEl?.dataset?.kcpDiscount ?? configEl?.dataset?.discount ?? '15';
  const isEnabled = enabledValue === 'true';
  const discount = Number.parseInt(discountValue, 10) || 15;

  return {
    enabled: isEnabled,
    discount
  };
}

/**
 * Check cart.js and hide/show the YMAL section based on whether
 * any of the hardcoded handles are already in the cart.
 * Called on every cart update event.
 */
async function syncYmalVisibility() {
  const section = document.querySelector('.cart--you-may-like[data-ymal-handles]');
  if (!section) return;

  try {
    const res  = await fetch('/cart.js', { credentials: 'same-origin' });
    const cart = await res.json();
    const cartHandles = new Set((cart.items || []).map(item => item.handle));
    const hasYmal = YMAL_HANDLES.some(h => cartHandles.has(h));
    section.style.display = hasYmal ? 'none' : '';
  } catch (e) {
    console.warn('[YMAL] Failed to sync visibility:', e);
  }
}

globalThis.addEventListener('theme:cart:updated', syncYmalVisibility);


class CartRecsCarousel extends HTMLElement {
  connectedCallback() {
    this._track    = this.querySelector('.cart--you-may-like__track');
    this._root     = this.closest('.cart--you-may-like');
    this._prevBtn  = this._root?.querySelector('.cart--you-may-like__arrow--prev');
    this._nextBtn  = this._root?.querySelector('.cart--you-may-like__arrow--next');

    if (!this._track) return;

    this._fetchProducts();
  }

  disconnectedCallback() {
    if (this._track) this._track.removeEventListener('scroll', this._onScroll);
    if (this._prevBtn) this._prevBtn.removeEventListener('click', this._onPrev);
    if (this._nextBtn) this._nextBtn.removeEventListener('click', this._onNext);
  }

  async _fetchProducts() {
    try {
      const base = Shopify?.routes?.root ?? '/';

      // Fetch all 3 products in parallel
      const results = await Promise.all(
        YMAL_HANDLES.map(handle =>
          fetch(`${base}products/${handle}.js`, { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        )
      );

      const products = results.filter(Boolean);

      if (!products.length) {
        if (this._root) this._root.style.display = 'none';
        return;
      }

      const metafields = JSON.parse(this.dataset.metafields || '{}');

      products.forEach(product => {
        const card = this._buildCard(product, metafields[product.handle] || '');
        this._track.appendChild(card);
      });

      this._initCarousel();
      this._bindAtcButtons();

      // Run initial visibility check
      syncYmalVisibility();
    } catch (err) {
      console.warn('[YMAL] Failed to fetch products:', err);
    }
  }

  _buildCard(product, metafield) {
    const variant = product.variants?.[0];
    const image = product.images?.[0];
    const currencyCode = globalThis.Shopify?.currency?.active ?? 'USD';
    const imgSrc = image || '';
    const available = variant ? variant.available : false;
    const variantId = variant ? variant.id : '';
    const kcpSettings = getKcpSettings(this);
    const listPriceCents = variant ? variant.price : 0;
    const salePriceCents = kcpSettings.enabled && listPriceCents > 0
      ? Math.floor((listPriceCents * (100 - kcpSettings.discount)) / 100)
      : listPriceCents;
    const displayPrice = (listPriceCents / 100).toFixed(2);
    const displaySalePrice = (salePriceCents / 100).toFixed(2);

    const priceMarkup = kcpSettings.enabled && listPriceCents > 0
      ? `
        <div class="cart-recs__price">
          <span class="cart-recs__price--current">
            <span class="currency-USD">${currencyCode}</span>&nbsp;<s>$${displayPrice}</s>
          </span>
          <span class="cart-recs__price--sale">
            <span class="currency-USD">${currencyCode}</span>&nbsp;$${displaySalePrice}
          </span>
        </div>
      `
      : `
        <div class="cart-recs__price">
          <span class="cart-recs__price--current"><span class="currency-USD">${currencyCode}</span>&nbsp;$${displayPrice}</span>
        </div>
      `;

    const card = document.createElement('div');
    card.className = 'cart-recs__card';
    card.innerHTML = `
      <a class="cart-recs__image-link" href="/products/${product.handle}">
        ${ imgSrc
          ? `<img class="cart-recs__image" src="${imgSrc}" alt="${product.title}" loading="lazy">`
          : `<div class="cart-recs__image-placeholder"></div>`
        }
      </a>
      <a class="cart-recs__title" href="/products/${product.handle}">${product.title}</a>
      ${ metafield ? `<div class="cart-recs__metafield">${metafield}</div>` : '' }
      ${priceMarkup}
      <button
        class="cart-recs__atc-btn"
        type="button"
        data-variant-id="${variantId}"
        ${ available ? '' : 'disabled' }
      >${ available ? 'ADD TO BAG' : 'Sold Out' }</button>
    `;
    return card;
  }

  _initCarousel() {
    if (!this._prevBtn || !this._nextBtn) return;

    this._onPrev   = () => this._scroll('prev');
    this._onNext   = () => this._scroll('next');
    this._onScroll = () => this._updateArrows();

    this._prevBtn.addEventListener('click', this._onPrev);
    this._nextBtn.addEventListener('click', this._onNext);
    this._track.addEventListener('scroll', this._onScroll, { passive: true });

    // Touch swipe support
    this._swipeStartX = 0;
    this._track.addEventListener('touchstart', (e) => {
      this._swipeStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    this._track.addEventListener('touchend', (e) => {
      const delta = e.changedTouches[0].screenX - this._swipeStartX;
      if (delta < -40) this._scroll('next');
      else if (delta > 40) this._scroll('prev');
    }, { passive: true });

    this._updateArrows();
  }

  _bindAtcButtons() {
    this._track.querySelectorAll('.cart-recs__atc-btn[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = Number.parseInt(btn.dataset.variantId, 10);
        if (!variantId || btn.disabled) return;

        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = '...';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });

          if (res.ok) {
            btn.textContent = 'Added!';
            globalThis.dispatchEvent(new CustomEvent('theme:cart:refresh'));
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          } else {
            btn.textContent = 'Error';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          }
        } catch (err) {
          console.warn('[YMAL] Add to cart failed:', err);
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
    });
  }

  _scroll(direction) {
    if (!this._track) return;
    const card = this._track.firstElementChild;
    if (!card) return;
    const cardWidth = card.offsetWidth + 12;
    this._track.scrollBy({ left: direction === 'next' ? cardWidth : -cardWidth, behavior: 'smooth' });
  }

  _updateArrows() {
    if (!this._track || !this._prevBtn || !this._nextBtn) return;
    const { scrollLeft, scrollWidth, clientWidth } = this._track;
    this._prevBtn.disabled = scrollLeft <= 2;
    this._nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
}

customElements.define('cart-recs-carousel', CartRecsCarousel);

